import React, { useState, useEffect, useRef } from "react";
import Typewriter from "typewriter-effect";
import { getYoutubeData, getYoutube } from "./api/youtubeApi";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import EmailLink from "./EmailLink";
import icons from "./data/icons";
import { getHome } from "./api/homeService";
import { getContact } from "./api/contactService";
import { getExperiences } from "./api/experienceService";
import { getPortfolio, getPublications, getCommunity } from "./api/portfolio.service";
import { getAbout } from "./api/aboutService";
Modal.setAppElement("#root");
const handleSmoothScroll = (e, sectionId) => {
  e.preventDefault();

  const section = document.getElementById(sectionId);
  if (section) {
    section.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  }
};
const FloatingBadges = ({ badges = [] }) => {
  return (
    <>
      {badges.map((badge, index) => (
        <div
          key={index}
          className={`floating-badge floating-badge-${badge.position}`}
          style={{
            position: "absolute",
            pointerEvents: "auto",
          }}
        >
          <div
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full
            bg-gradient-to-r ${badge.color}
            backdrop-blur-lg border border-white/20
            shadow-2xl hover:scale-105 transition-all duration-300`}
          >
            <span className="text-base">{badge.icon}</span>
            <span className="text-white text-xs font-semibold whitespace-nowrap">
              {badge.name}
            </span>
          </div>
        </div>
      ))}

      <style jsx>{`
        /* FLOAT UP */
        @keyframes floatUp {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(calc(-1 * var(--float-distance)));
          }
        }

        /* FLOAT DOWN */
        @keyframes floatDown {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(var(--float-distance));
          }
        }

        .floating-badge {
          z-index: 3000;
        }

        /* TOP LEFT → FLOAT UP */
        .floating-badge-top-left {
          top: 20%;
          left: -10%;
          --float-distance: 15px;
          animation: floatUp 8s ease-in-out infinite;
        }

        /* TOP RIGHT → FLOAT DOWN */
        .floating-badge-top-right {
          top: 45%;
          right: -5%;
          --float-distance: 18px;
          animation: floatDown 8.5s ease-in-out infinite;
        }

        /* BOTTOM LEFT → FLOAT DOWN */
        .floating-badge-bottom-left {
          bottom: 20%;
          left: -7%;
          --float-distance: 16px;
          animation: floatDown 7.5s ease-in-out infinite;
        }

        /* RESPONSIVE */
        @media (max-width: 768px) {
          .floating-badge-top-left {
            left: -10%;
            top: 20%;
            --float-distance: 10px;
          }

          .floating-badge-top-right {
            right: -6%;
            top: 30%;
            --float-distance: 12px;
          }

          .floating-badge-bottom-left {
            left: -8%;
            bottom: 20%;
            --float-distance: 11px;
          }

          .floating-badge > div {
            padding: 0.1rem 0.625rem;
            gap: 0.25rem;
          }

          .floating-badge span.text-base {
            font-size: 0.75rem;
          }

          .floating-badge span.text-xs {
            font-size: 0.5rem;
          }
        }

        @media (max-width: 480px) {
          .floating-badge-top-left {
            left: -5%;
            top: 20%;
          }

          .floating-badge-top-right {
            right: -5%;
            top: 28%;
          }

          .floating-badge-bottom-left {
            left: -5%;
            bottom: 20%;
          }

          .floating-badge > div {
            padding: 0.01rem 0.3rem;
          }
        }
      `}</style>
    </>
  );
};
const Home = () => {
  const visibleItems = useRef([]);
  const startXRef = useRef(0);
  const [isScrollIconVisible, setIsScrollIconVisible] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentRole, setCurrentRole] = useState("");
  const [showEmailMenu, setShowEmailMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoveredStat, setHoveredStat] = useState(null);
  const [isStatHovered, setIsStatHovered] = useState(false);
  const [hoveredSlide, setHoveredSlide] = useState(null);
  const [experienceData, setExperienceData] = useState([]);
  const [skillCategories, setSkillCategories] = useState({});
  const [contactData, setContactData] = useState(null);
  const [homeData, setHomeData] = useState(null);
  const [skillLabels, setSkillLabels] = useState([]);
  useEffect(() => {
    const loadHome = async () => {
      try {
        const response = await getHome();

        const home = response?.data?.data?.[0];

        if (home) {
          setHomeData(home);
        }
      } catch (error) {
        console.error("Error loading Home data:", error);
      }
    };

    loadHome();
  }, []);
  useEffect(() => {
    const loadContact = async () => {
      try {
        const response = await getContact();

        const contact = response?.data?.data;

        setContactData(
          Array.isArray(contact) ? contact[0] : contact
        );
      } catch (error) {
        console.error("Failed to load contact data:", error);
      }
    };

    loadContact();
  }, []);

  const handlePortfolioNavigation = (e, button) => {
    const action = button?.action || "filter";

    // FILTER
    if (action === "filter") {
      const filter = button?.filter;

      if (!filter) return;

      handleSmoothScroll(e, "projects");

      window.dispatchEvent(
        new CustomEvent("portfolioFilterChange", {
          detail: { filter },
        })
      );

      window.dispatchEvent(
        new CustomEvent("navbarProjectsClick", {
          detail: { section: filter },
        })
      );

      return;
    }

    // SCROLL
    if (action === "scroll") {
      const target = button?.target;

      if (!target) return;

      handleSmoothScroll(e, target);
      return;
    }

    // ROUTE
    if (action === "route") {
      const route = button?.route;

      if (!route) return;

      window.location.href = route;
      return;
    }
  };
  useEffect(() => {
    const loadAboutSkills = async () => {
      try {
        const res = await getAbout();

        const skills = res.data.data?.skills || [];
        const skillLabels = res.data.data?.about?.[0]?.skillLabels || [];

        const groupedSkills = skills.reduce((acc, skill) => {
          if (!acc[skill.category]) {
            acc[skill.category] = [];
          }

          acc[skill.category].push(skill);

          return acc;
        }, {});

        setSkillCategories(groupedSkills);
        setSkillLabels(skillLabels);
      } catch (error) {
        console.error("Error loading skills:", error);
      }
    };

    loadAboutSkills();
  }, []);
  useEffect(() => {
    const loadExperiences = async () => {
      try {
        const res = await getExperiences();
        setExperienceData(res.data.data || []);
      } catch (err) {
        console.error(err);
      }
    };

    loadExperiences();
  }, []);
  const publicExperienceData = experienceData.filter(
    company =>
      company.positions?.some(
        position => position.countExperience !== false
      )
  );
  const menuRef = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    const loadPortfolioStats = async () => {
      try {
        const [portfolioRes, publicationRes, communityRes] = await Promise.all([
          getPortfolio(),
          getPublications(),
          getCommunity(),
        ]);

        const projects = portfolioRes.data.data || [];
        const publications = publicationRes.data.data || [];
        const communities = communityRes.data.data || [];

        setTotalProjectCount(projects.length + publications.length + communities.length);

        setPortfolioBreakdown([
          `Projects (${projects.length})`,
          `Publications (${publications.length})`,
          `Community (${communities.length})`,
        ]);
      } catch (err) {
        console.error("Error loading portfolio stats:", err);
      }
    };

    loadPortfolioStats();
  }, []);
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowEmailMenu(false);
      }
    }

    if (showEmailMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmailMenu]);

  const [youtubeInfo, setYoutubeInfo] = useState({
    title: "YouTube",
    description: "",
    subscribers: "0",
    videos: "0",
    views: "0",
    avatar: "",
    socialLinks: [],
    isActive: false,
  });

  // Calculate total project count from all sources
  const [portfolioBreakdown, setPortfolioBreakdown] = useState([]);
  const [totalProjectCount, setTotalProjectCount] = useState(0);
  const totalskillsCount = Object.values(skillCategories)
    .reduce((sum, arr) => sum + arr.length, 0);
  // Dynamic data
  const [portfolioData, setPortfolioData] = useState({
    projectCount: totalProjectCount,
    skillCount: 0,
    currentWork: {
      title: "Loading...",
      company: "Loading...",
      description: "Fetching data...",
      duration: "...",
    },
  });

  // Check if mobile
  useEffect(() => {
    const checkMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(
      navigator.userAgent
    );
    setIsMobile(checkMobile);
  }, []);

  useEffect(() => {
    const loadYoutube = async () => {
      try {
        const response = await getYoutubeData();

        const data = response?.data?.data;
        const channel = data?.channel;
        const config = data?.config;

        setYoutubeInfo({
          title: channel?.title || "",
          description: channel?.description || "",
          subscribers: channel?.subscribers || "0",
          videos: channel?.videos || "0",
          views: channel?.totalViews || "0",
          avatar: channel?.avatar || "",
          socialLinks: config?.socialLinks || [],
          isActive: config?.isActive === true,
        });
      } catch (error) {
        console.error("Failed to load YouTube data:", error);
      }
    };

    loadYoutube();
  }, []);
  const calculateDuration = (duration) => {
    if (!duration) return "Present";

    const parts = duration.split(" - ");
    if (parts.length !== 2) return duration;

    const [startStr, endStr] = parts;

    const parseDate = (dateStr) => {
      const monthMap = {
        January: 0,
        February: 1,
        March: 2,
        April: 3,
        May: 4,
        June: 5,
        July: 6,
        August: 7,
        September: 8,
        October: 9,
        November: 10,
        December: 11,
      };

      const [month, year] = dateStr.trim().split(" ");
      return new Date(parseInt(year), monthMap[month] || 0, 1);
    };

    const start = parseDate(startStr);
    const end = endStr.toLowerCase().includes("present")
      ? new Date()
      : parseDate(endStr);

    const diffMonths =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth());

    const years = Math.floor(diffMonths / 12);
    const months = diffMonths % 12;

    if (years === 0 && months === 0) {
      return "Less than a month";
    } else if (years === 0) {
      return `${months} month${months !== 1 ? "s" : ""}`;
    } else if (months === 0) {
      return `${years} year${years !== 1 ? "s" : ""}`;
    } else {
      return `${years} year${years !== 1 ? "s" : ""} ${months} month${months !== 1 ? "s" : ""
        }`;
    }
  };

  // Parse "Month YYYY" to Date
  const parseMonthYear = (dateStr) => {
    const monthMap = {
      January: 0, February: 1, March: 2, April: 3, May: 4, June: 5,
      July: 6, August: 7, September: 8, October: 9, November: 10, December: 11,
    };
    const trimmed = dateStr.trim();
    const [month, year] = trimmed.split(" ");
    return new Date(parseInt(year), monthMap[month] ?? 0, 1);
  };

  // Keep your existing getLatestPosition() - works fine
  const getLatestPosition = () => {
    if (!publicExperienceData || publicExperienceData.length === 0) return null;
    const allPositions = [];
    publicExperienceData.forEach((company) => {
      company.positions.forEach((pos) => {
        const [, endStr] = pos.duration.split(" - ");
        const endDate = endStr.toLowerCase().includes("present")
          ? new Date()
          : parseMonthYear(endStr);
        allPositions.push({ ...pos, company: company.company, _endDate: endDate });
      });
    });
    allPositions.sort((a, b) => b._endDate - a._endDate);
    return allPositions[0];
  };

  const getTotalExperienceMonths = (publicExperienceData) => {
    const parse = (str) =>
      str.toLowerCase().includes("present")
        ? new Date()
        : new Date(`${str.split(" ")[0]} 1, ${str.split(" ")[1]}`);

    const ranges = [];

    publicExperienceData.forEach(c =>
      c.positions.forEach(pos => {
        const [s, e] = pos.duration.split(" - ").map(v => v.trim());
        ranges.push({ start: parse(s), end: parse(e) });
      })
    );

    ranges.sort((a, b) => a.start - b.start);

    const merged = [];
    for (const r of ranges) {
      const last = merged[merged.length - 1];
      if (last && r.start <= last.end) {
        last.end = new Date(Math.max(last.end, r.end));
      } else {
        merged.push(r);
      }
    }
    const diffMonthsInclusive = (start, end) =>
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth()) + 1;
    return merged.reduce(
      (sum, r) => sum + diffMonthsInclusive(r.start, r.end),
      0
    );

  };


  const formatTotalExperience = (months) => {
    if (months <= 0) return "0 Months Experience";

    const years = Math.floor(months / 12);
    const remMonths = months % 12;

    // 1. <12 months → (months-1)+ months
    if (years === 0) return `${months - 1}+ Months Experience`;

    // 2. Exactly 12 months → "10+ months"
    if (months === 12) return "10+ Months Experience";

    // 3. 13-23 months → Exact "1 year X months"
    if (years === 1 && remMonths > 0) return `1 Year ${remMonths} Months Experience`;

    // 4. 24+ months → Just "(years-1)+ years" (NO months)
    const displayYears = years - 1;
    return `${displayYears}+ Years experience`;
  };

  const totalExperienceMonths = getTotalExperienceMonths(publicExperienceData);
  const totalExperienceText = formatTotalExperience(totalExperienceMonths);

  const fetchPortfolioDataFromDOM = () => {
    try {
      let currentJob = {
        title: "Not Found",
        company: "Not Found",
        description: "No experience data available",
        duration: "...",
      };

      const latestPosition = getLatestPosition();

      if (latestPosition) {
        currentJob = {
          title: latestPosition.title,
          company: latestPosition.company,
          description: latestPosition.description?.[0],
          duration: latestPosition.duration,
        };
      }

      setPortfolioData({
        projectCount: totalProjectCount,
        skillCount: totalskillsCount,
        currentWork: currentJob,
      });
    } catch (error) {
      console.error("Error fetching portfolio data:", error);
    }
  };

  useEffect(() => {
    fetchPortfolioDataFromDOM();
    const timer = setTimeout(() => {
      fetchPortfolioDataFromDOM();
    }, 100);
    return () => clearTimeout(timer);
  }, [totalProjectCount, totalskillsCount]);
  const skillBreakdown = Object.keys(skillCategories)
    .map(key => {
      const category = skillLabels.find(
        item => item.key === key
      );

      const label =
        category?.label ||
        key
          .replace(/([A-Z])/g, " $1")
          .trim()
          .replace(/\b\w/g, char => char.toUpperCase());

      const count =
        skillCategories[key]?.length || 0;

      return `${label} (${count})`;
    })
    .join(" • ");
  const laptopSlides = [
    {
      type: "profile",
      name: homeData?.laptopName || "",
      role: currentRole,
      description: homeData?.description || "",
    },

    {
      type: "experience",
      title: portfolioData.currentWork.title,
      company: portfolioData.currentWork.company,
      duration: portfolioData.currentWork.duration,
      color: "from-blue-500 to-cyan-500",
    },

    {
      type: "stats",
      items: [
        {
          icon: (
            <icons.FaStar className="text-base sm:text-xl md:text-2xl text-white flex-shrink-0" />
          ),
          label: "Key Accomplishments",
          count: `${portfolioData.projectCount - 2}+`,
          breakdown: portfolioBreakdown,
          color: "from-blue-500 to-cyan-500",
        },

        {
          icon: (
            <icons.Sparkle className="text-base sm:text-xl md:text-2xl text-white flex-shrink-0" />
          ),
          label: "Competencies",
          count: `${portfolioData.skillCount - 10}+`,
          breakdown: skillBreakdown,
          color: "from-purple-500 to-pink-500",
        },
      ],
    },
...(youtubeInfo?.isActive === true
    ? [
    {
      type: "card",

      icon: "FaYoutube",

      // FROM YOUTUBE DATABASE/API
      title: youtubeInfo?.title || "YouTube Channel",

      description: youtubeInfo?.description || "",

      avatar: youtubeInfo?.avatar,

      // FROM youtube.socialLinks DATABASE COLUMN
      socials: youtubeInfo?.socialLinks || [],

      color: "from-red-600 to-red-400",

      showButton: true,

      buttonText: "see more",

      target: "youtube",

      items: [
        {
          label: "Subscribers",
          value: youtubeInfo?.subscribers || "0",
        },

        {
          label: "Videos",
          value: youtubeInfo?.videos || "0",
        },

        {
          label: "Views",
          value: youtubeInfo?.views || "0",
        },
      ],
    },
  ]
  : []),
  ];


  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrollIconVisible(scrollPosition < 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 100);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!homeData?.roles?.length) return;

    setCurrentRole(homeData.roles[0]);

    const interval = setInterval(() => {
      setCurrentRole((prevRole) => {
        const currentIndex = homeData.roles.indexOf(prevRole);
        const nextIndex =
          (currentIndex + 1) % homeData.roles.length;

        return homeData.roles[nextIndex];
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [homeData?.roles]);
  useEffect(() => {
    if (isStatHovered) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % laptopSlides.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [laptopSlides.length, isStatHovered]);

  const scrollToAbout = () => {
    const aboutSection = document.getElementById("about");
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  const handleViewClick = (target) => {
    const section = document.getElementById(target);

    if (section) {
      section.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  if (!homeData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0C121D]">
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-indigo-500/30 blur-xl"></div>
            <div className="relative h-14 w-14 animate-spin rounded-full border-[6px] border-slate-700 border-t-indigo-500"></div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      id="home"
      className="bg-fade-animation min-h-screen w-full overflow-x-hidden flex items-center justify-center pt-24 sm:pt-20 pb-16"
      style={{
        backgroundColor: "#0C121D",
        backgroundImage: `
  /* 🔝 TOP – mixed colors */
  radial-gradient(at 50% 0%, hsla(25, 100%, 50%, 0.18) 0, transparent 45%),
  radial-gradient(at 20% 0%, hsla(260, 80%, 40%, 0.22) 0, transparent 50%),
  radial-gradient(at 80% 0%, hsla(200, 90%, 45%, 0.18) 0, transparent 50%),

  /* ↔ LEFT & RIGHT – soft orange */
  radial-gradient(at 0% 60%, hsla(25, 100%, 50%, 0.15) 0, transparent 40%),
  radial-gradient(at 100% 60%, hsla(25, 100%, 50%, 0.15) 0, transparent 40%),

  /* 🎯 CENTER – subtle depth */
  radial-gradient(at 50% 45%, hsla(200, 90%, 20%, 0.18) 0, transparent 60%)

        `,
      }}
    >
      <main className="max-w-full mx-auto w-full px-0 sm:px-10 lg:px-10 xl:px-20">
        <section
          className="flex flex-col lg:grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          ref={(el) => (visibleItems.current[0] = el)}
        >
          {/* Text Content */}
          <div
            ref={(el) => (visibleItems.current[1] = el)}
            className="transition-all duration-700 opacity-100 translate-y-0 order-1 lg:order-1 text-center lg:text-left w-full"
          >
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-slate-800/50 border border-emerald-500/30 mb-4 sm:mb-6">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              <span className="text-emerald-400 text-xs sm:text-sm font-medium">
                {homeData?.opportunities}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 sm:mb-4">
              {homeData?.name}
            </h1>

            <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-4 sm:mb-6 min-h-[2.5rem] sm:min-h-[3rem]">
              <Typewriter
                options={{
                  strings: homeData?.roles || [],
                  autoStart: true,
                  loop: true,
                  delay: 75,
                  deleteSpeed: 50,
                  pauseFor: 1500,
                }}
              />
            </div>
            {/* Contact Info */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 mb-6 sm:mb-8 items-center lg:items-start justify-center lg:justify-start">
              <div className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl bg-slate-800/50 border border-slate-700/50">
                <icons.HiOutlineLocationMarker className="text-cyan-400 text-sm sm:text-base" />
                <span className="text-slate-300 text-xs sm:text-sm">
                  {contactData?.location}
                </span>
              </div>

              <div className="relative">
                <EmailLink
                  email={contactData?.email}
                  subject={contactData?.emailSubject || ""}
                  body={contactData?.emailBody || ""}
                  className="
      flex items-center gap-2
      px-3 sm:px-4 py-2
      rounded-xl
      bg-slate-800/50
      border border-slate-700/50
      hover:border-cyan-500/50
      transition-all duration-300
      text-slate-300
      text-xs sm:text-sm
    "
                >
                  <icons.HiOutlineMail className="text-cyan-400 text-sm sm:text-base" />

                  <span>
                    <span className="text-slate-200 font-medium">
                      Send Email:
                    </span>{" "}
                    For any inquiries
                  </span>

                  <icons.HiChevronDown className="w-4 h-4 text-slate-400" />
                </EmailLink>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-4 items-center justify-center lg:justify-start">

              {/* View Details */}
              {/* View Details */}
              {/* View Details */}
              <button
                type="button"
                onClick={(e) =>
                  handlePortfolioNavigation(
                    e,
                    homeData?.buttons?.viewDetails
                  )
                }
                className="
    cursor-pointer
    group inline-flex items-center justify-center gap-1.5
    px-8 py-2 sm:px-6 sm:py-3
    bg-orange-600/50
    border border-slate-700/50
    text-slate-300 font-medium
    rounded-lg sm:rounded-xl
    transition-all duration-300
    text-xs sm:text-sm md:text-base
    hover:text-white
    hover:border-cyan-400/60
    hover:bg-gradient-to-r
    hover:from-cyan-500/20
    hover:to-blue-500/20
    hover:shadow-lg
    hover:shadow-cyan-500/25
    hover:scale-105
  "
              >
                {homeData?.viewDetails}

                <icons.FaChevronCircleDown
                  className="
      text-xs sm:text-sm
      group-hover:text-cyan-300
      group-hover:rotate-12
      group-hover:scale-110
      transition-all duration-300
      ease-out
    "
                />
              </button>

              {/* View Explore */}
              <button
                type="button"
                onClick={(e) =>
                  handlePortfolioNavigation(
                    e,
                    homeData?.buttons?.viewExplore
                  )
                }
                className="
    cursor-pointer
    group inline-flex items-center justify-center gap-1.5
    px-8 py-2 sm:px-6 sm:py-3
    bg-blue-600/50
    border border-slate-700/50
    text-slate-300 font-medium
    rounded-lg sm:rounded-xl
    transition-all duration-300
    text-xs sm:text-sm md:text-base
    hover:text-white
    hover:border-cyan-400/60
    hover:bg-gradient-to-r
    hover:from-cyan-500/20
    hover:to-blue-500/20
    hover:shadow-lg
    hover:shadow-cyan-500/25
    hover:scale-105
  "
              >
                {homeData?.ViewExplore}

                <icons.FaChevronCircleDown
                  className="
      text-xs sm:text-sm
      group-hover:text-cyan-300
      group-hover:rotate-12
      group-hover:scale-110
      transition-all duration-300
      ease-out
    "
                />
              </button>
            </div>
          </div>

          {/* Laptop Mockup */}
          <div
            ref={(el) => (visibleItems.current[2] = el)}
            className="flex justify-center lg:justify-end transition-all duration-700 delay-200 opacity-100 translate-y-0 order-2 lg:order-2 w-full max-w-full"
          >
            {/* THIS WRAPPER PREVENTS HORIZONTAL SCROLL */}
            <div className="w-full max-w-[90%] sm:max-w-[500px] lg:max-w-[600px] xl:max-w-[650px] mx-auto flex justify-center">
              <div className="relative group overflow-visible" style={{ width: '100%', maxWidth: '650px' }}>

                {/* ADD FLOATING BADGES HERE - BOTH SIDES */}
                <FloatingBadges badges={homeData?.badges} />
                {/* Main Screen */}
                <div
                  className="w-full aspect-[16/10] bg-cover bg-center bg-no-repeat rounded-t-xl sm:rounded-t-2xl border-[5px] sm:border-[6px] md:border-[7px] lg:border-[8px] border-black shadow-2xl overflow-hidden flex flex-col relative"
                  onTouchStart={(e) => {
                    startXRef.current = e.touches[0].clientX;
                  }}
                  onTouchEnd={(e) => {
                    const endX = e.changedTouches[0].clientX;
                    const diff = endX - startXRef.current;

                    if (Math.abs(diff) < 40) return;

                    if (diff > 0) {
                      setCurrentSlide((prev) =>
                        prev === 0 ? laptopSlides.length - 1 : prev - 1
                      );
                    } else {
                      setCurrentSlide((prev) =>
                        (prev + 1) % laptopSlides.length
                      );
                    }
                  }} style={{
                    touchAction: "pan-y",

                    backgroundImage: `
                    linear-gradient(
                      135deg,
                    rgb(7, 7, 7) 0%,
                    rgba(0, 0, 0, 0.97) 25%,
                    rgba(5, 5, 5, 0.95) 50%,
                    rgba(11, 11, 12, 0.99) 100%
                    ),
                    url(${homeData?.laptopBg || ""})
                  `,
                    backgroundBlendMode: 'soft-light',
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                  }}
                >
                  {/* AUTO GLASS SWEEP */}
                  <div className="glass-sweep"></div>

                  <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Status Bar WITH DATE - TIME NEAR DATE */}
                    <div className="flex items-center justify-between px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 flex-shrink-0 bg-black relative z-40">
                      {/* Left - Date & Time Together */}
                      <div className="flex items-center gap-0.5 sm:gap-1">
                        <span className="animate-pulse text-white
    text-[8px] sm:text-[9px] md:text-[10px]
    font-semibold">
                          {formatDate(currentTime)}
                        </span>

                        <span className="text-slate-400
    text-[8px] sm:text-[9px] md:text-[10px]">
                          •
                        </span>

                        <span className="animate-pulse text-white
    text-[8px] sm:text-[9px] md:text-[10px]
    font-semibold">
                          {formatTime(currentTime)}
                        </span>
                      </div>


                      {/* Right - Battery */}
                      <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2">
                        {/* Charging dots */}
                        <div className="flex gap-0.5">
                          <span className="blink-dot d1"></span>
                          <span className="blink-dot d2"></span>
                          <span className="blink-dot d3"></span>
                        </div>

                        {/* Battery */}
                        <div className="flex items-center">
                          <div className="w-4 h-2 sm:w-5 sm:h-2.5 md:w-6 md:h-3 border border-white rounded-full relative">
                            <div className="absolute inset-0.5 bg-emerald-400 rounded-full animate-pulse"></div>
                          </div>
                          <div className="w-0.5 h-1 sm:h-1.5 bg-white rounded-r-sm ml-0.5"></div>
                        </div>
                      </div>

                    </div>

                    {/* ANIMATED DOTS BACKGROUND */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                      <div className="dot-particle dot-1"></div>
                      <div className="dot-particle dot-2"></div>
                      <div className="dot-particle dot-3"></div>
                      <div className="dot-particle dot-4"></div>
                      <div className="dot-particle dot-5"></div>
                      <div className="dot-particle dot-6"></div>
                      <div className="dot-particle dot-7"></div>
                      <div className="dot-particle dot-8"></div>
                      <div className="dot-particle dot-9"></div>
                      <div className="dot-particle dot-10"></div>
                    </div>

                    {/* Slideshow Container */}
                    <div className="flex-1 relative overflow-hidden min-h-0 px-1 sm:px-2 md:px-3 py-1 sm:py-2 md:py-3">
                      {laptopSlides.map((slide, index) => {
                        const isSlideHovered = hoveredSlide === index;
                        const isCurrentSlide = currentSlide === index;

                        return (
                          <div
                            key={index}
                            onMouseEnter={() => setHoveredSlide(index)}
                            onMouseLeave={() => setHoveredSlide(null)}
                            className={`
          absolute inset-0 
          transition-all duration-700 ease-in-out 
          flex items-center justify-center 
          px-2 sm:px-3 md:px-4 py-2 sm:py-3 md:py-4
          ${isCurrentSlide ? "opacity-100 translate-y-0" : "opacity-0 translate-y-full pointer-events-none"}
          ${isSlideHovered && isCurrentSlide ? "scale-[1.03] -translate-y-1 shadow-2xl shadow-cyan-500/30" : "scale-100"}
        `}
                            style={{
                              zIndex: isCurrentSlide ? 60 : 40,
                              pointerEvents: isCurrentSlide ? 'auto' : 'none' // Only current slide is interactive
                            }}
                          >
                            {/* Rest of your slide content remains the same */}
                            {/* SLIDE 1 - PROFILE */}
                            {slide.type === "profile" && (
                              <div className="w-full h-full flex items-center justify-center px-1 sm:px-2 md:px-3">

                                <div
                                  className="
        group relative overflow-hidden

        w-[90%]
        sm:w-[88%]
        md:w-[90%]
        lg:w-[92%]

        rounded-xl
        sm:rounded-2xl

        border border-white/10
        hover:border-cyan-400/30

        bg-gradient-to-br
        from-slate-900
        via-slate-800
        to-blue-950/80

        backdrop-blur-xl

        px-2
        py-2
        sm:px-3
        sm:py-2.5
        md:px-4
        md:py-3

        flex flex-col
        items-center
        justify-center

        shadow-xl
        shadow-black/30

        transition-all
        duration-500

        hover:-translate-y-1
        hover:shadow-cyan-500/10
      "
                                >

                                  {/* ───────── BACKGROUND EFFECTS ───────── */}

                                  <div
                                    className="
          absolute
          -top-16
          -right-16
          w-32
          h-32
          rounded-full
          bg-cyan-500/10
          blur-3xl
          group-hover:bg-cyan-400/20
          transition-all
          duration-500
        "
                                  />

                                  <div
                                    className="
          absolute
          -bottom-16
          -left-16
          w-32
          h-32
          rounded-full
          bg-blue-600/10
          blur-3xl
        "
                                  />

                                  {/* Decorative lines */}
                                  <div
                                    className="
          absolute
          top-0
          left-0
          w-16
          h-px
          bg-gradient-to-r
          from-cyan-400
          to-transparent
          opacity-60
        "
                                  />

                                  <div
                                    className="
          absolute
          bottom-0
          right-0
          w-16
          h-px
          bg-gradient-to-l
          from-purple-400
          to-transparent
          opacity-60
        "
                                  />


                                  {/* ───────── LOGO ───────── */}

                                  <div className="relative z-10 mb-1 sm:mb-1.5">

                                    {/* Outer glow */}
                                    <div
                                      className="
            absolute
            inset-[-5px]
            rounded-xl
            sm:rounded-2xl
            bg-gradient-to-r
            from-blue-500
            via-cyan-400
            to-purple-500
            opacity-20
            blur-md
            group-hover:opacity-40
            transition-all
            duration-500
          "
                                    />

                                    {/* Logo container */}
                                    <div
                                      className="
            relative

            w-10
            h-10

            sm:w-13
            sm:h-13

            md:w-16
            md:h-16

            rounded-xl
            sm:rounded-2xl

            p-[2px]

            bg-gradient-to-br
            from-blue-500
            via-cyan-400
            to-purple-500

            shadow-lg

            group-hover:scale-105

            transition-transform
            duration-500
          "
                                    >

                                      <div
                                        className="
              w-full
              h-full

              rounded-[10px]
              sm:rounded-[14px]

              bg-slate-900

              flex
              items-center
              justify-center

              overflow-hidden
            "
                                      >
                                        {homeData?.logo ? (
                                          <img
                                            src={homeData.logo}
                                            alt={homeData?.logoAlt || "Logo"}
                                            className="
                  w-[75%]
                  h-[75%]
                  object-contain
                "
                                          />
                                        ) : (
                                          <span
                                            className="
                  text-white
                  font-bold
                  text-sm
                  sm:text-lg
                  md:text-xl
                "
                                          >
                                            {slide.name?.charAt(0)}
                                          </span>
                                        )}
                                      </div>

                                    </div>

                                    {/* Online indicator */}
                                    <span
                                      className="
            absolute
            -right-0.5
            -bottom-0.5

            w-3
            h-3
            sm:w-3.5
            sm:h-3.5

            rounded-full

            bg-emerald-400

            border-2
            border-slate-900

            shadow-[0_0_8px_rgba(52,211,153,0.7)]
          "
                                    />
                                  </div>


                                  {/* ───────── NAME ───────── */}

                                  <div className="relative z-10 text-center w-full">

                                    <h3
                                      className="
            font-extrabold

            text-sm
            sm:text-base
            md:text-lg
            lg:text-xl

            leading-tight

            bg-gradient-to-r
            from-white
            via-cyan-100
            to-cyan-300

            bg-clip-text
            text-transparent

            truncate
          "
                                    >
                                      {slide.name}
                                    </h3>


                                    {/* ───────── ROLE BADGE ───────── */}

                                    <div className="flex justify-center mt-1">

                                      <div
                                        className="
              inline-flex
              items-center
              gap-1

              px-2
              py-0.5

              rounded-full

              bg-cyan-400/10
              border border-cyan-400/20

              text-cyan-300

              text-[7px]
              sm:text-[8px]
              md:text-[9px]

              font-semibold

              whitespace-nowrap
            "
                                      >

                                        <span
                                          className="
                w-1
                h-1
                rounded-full
                bg-cyan-400
                shadow-[0_0_5px_rgba(34,211,238,0.8)]
              "
                                        />

                                        {currentRole}

                                      </div>

                                    </div>


                                    {/* ───────── DESCRIPTION ───────── */}

                                    {slide.description && (
                                      <p
                                        className="
              mt-1.5
              sm:mt-2

              px-1
              sm:px-2

              text-slate-400

              text-[10px]
              sm:text-[8px]
              md:text-[9px]
              lg:text-[10px]

              leading-relaxed

              line-clamp-2
            "
                                      >
                                        {slide.description}
                                      </p>
                                    )}

                                  </div>


                                  {/* ───────── DIVIDER ───────── */}

                                  <div
                                    className="
          relative
          z-10

          w-full

          flex
          items-center
          gap-2

          mt-1.5
          sm:mt-2
        "
                                  >

                                    <div
                                      className="
            flex-1
            h-px
            bg-gradient-to-r
            from-transparent
            to-slate-700
          "
                                    />

                                    <div
                                      className="
            w-1
            h-1
            rounded-full
            bg-cyan-400
            shadow-[0_0_6px_rgba(34,211,238,0.7)]
          "
                                    />

                                    <div
                                      className="
            flex-1
            h-px
            bg-gradient-to-l
            from-transparent
            to-slate-700
          "
                                    />

                                  </div>
                                </div>
                              </div>
                            )}

                            {/* SLIDE 2 - EXPERIENCE */}
                            {slide.type === "experience" && (
                              <div className="w-full h-full flex items-center justify-center px-1 sm:px-2 md:px-4">
                                <div
                                  className="
        relative w-[90%] sm:w-[88%] md:w-[90%]
        overflow-hidden
        rounded-xl sm:rounded-2xl
        border border-cyan-400/20
        bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950
        p-3 sm:p-4 md:p-6
        flex flex-col items-center justify-center
        shadow-lg shadow-blue-500/10
        transition-all duration-500
        hover:scale-[1.02]
        hover:shadow-xl hover:shadow-cyan-500/20
      "
                                >
                                  {/* Background Glow */}
                                  <div className="absolute -top-10 -right-10 w-24 h-24 sm:w-32 sm:h-32 bg-cyan-500/10 rounded-full blur-2xl" />
                                  <div className="absolute -bottom-10 -left-10 w-24 h-24 sm:w-32 sm:h-32 bg-blue-500/10 rounded-full blur-2xl" />

                                  {/* Icon */}
                                  <div className="relative z-10">
                                    <div
                                      className={`
            w-11 h-11
            sm:w-14 sm:h-14
            md:w-16 md:h-16
            bg-gradient-to-br ${slide.color}
            rounded-xl sm:rounded-2xl
            flex items-center justify-center
            shadow-lg shadow-blue-500/20
            ring-4 ring-white/5
          `}
                                    >
                                      <icons.Briefcase
                                        className="
              text-white
              w-5 h-5
              sm:w-6 sm:h-6
              md:w-7 md:h-7
            "
                                      />
                                    </div>

                                    {/* Glow */}
                                    <div
                                      className={`
            absolute inset-0
            bg-gradient-to-br ${slide.color}
            rounded-xl sm:rounded-2xl
            blur-xl opacity-30 -z-10
          `}
                                    />
                                  </div>

                                  {/* Experience */}
                                  <div className="relative z-10 text-center w-full mt-2 sm:mt-3">
                                    <p className="text-[9px] sm:text-[10px] md:text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">
                                      Professional Experience
                                    </p>

                                    <h3
                                      className="
            text-md
            sm:text-md
            md:text-2xl
            lg:text-2xl
            font-bold
            text-white
            leading-tight
            bg-gradient-to-r from-white via-cyan-100 to-cyan-300
            bg-clip-text text-transparent
          "
                                    >
                                      {totalExperienceText}
                                    </h3>

                                    {/* Small accent line */}
                                    <div className="mx-auto mt-2 sm:mt-3 h-1 w-10 sm:w-14 rounded-full bg-gradient-to-r from-blue-500 to-cyan-400" />
                                  </div>
                                </div>
                              </div>
                            )}
                            {/* SLIDE 3 - STATS */}
                            {slide.type === "stats" && (
                              <div className="w-full space-y-1.5 sm:space-y-2 md:space-y-2.5">
                                {slide.items.map((stat, i) => {
                                  const isHovered = hoveredStat === i;

                                  const breakdownArray = Array.isArray(stat.breakdown)
                                    ? stat.breakdown
                                    : typeof stat.breakdown === "string"
                                      ? stat.breakdown.split(" • ")
                                      : [];

                                  const hasMoreThan6 = breakdownArray.length > 6;
                                  const extraCount = Math.max(0, breakdownArray.length - 6);

                                  return (
                                    <div
                                      key={i}
                                      onMouseEnter={() => {
                                        setHoveredStat(i);
                                        setIsStatHovered(true);
                                      }}
                                      onMouseLeave={() => {
                                        setHoveredStat(null);
                                        setIsStatHovered(false);
                                      }}
                                      className={`
            group relative overflow-hidden
            rounded-lg sm:rounded-xl
            border
            p-1.5 sm:p-2 md:p-2.5
            backdrop-blur-md
            shadow-lg
            transition-all duration-300 ease-out
            ${isHovered
                                          ? "border-cyan-400/40 bg-slate-800/95 scale-[1.02] shadow-cyan-500/10"
                                          : "border-slate-700/60 bg-slate-800/70"
                                        }
          `}
                                    >
                                      {/* Background glow */}
                                      <div
                                        className={`
              absolute -right-8 -top-8
              w-16 h-16 sm:w-20 sm:h-20
              rounded-full blur-2xl
              bg-gradient-to-br ${stat.color}
              transition-opacity duration-300
              ${isHovered ? "opacity-20" : "opacity-5"}
            `}
                                      />

                                      <div className="relative z-10 flex items-center gap-2 sm:gap-2.5 md:gap-3">

                                        {/* Icon */}
                                        <div className="relative flex-shrink-0">
                                          <div
                                            className={`
                  w-8 h-8
                  sm:w-10 sm:h-10
                  md:w-11 md:h-11
                  bg-gradient-to-br ${stat.color}
                  rounded-lg sm:rounded-xl
                  flex items-center justify-center
                  shadow-lg
                  ring-1 ring-white/10
                  transition-all duration-300
                  ${isHovered
                                                ? "scale-110 shadow-xl"
                                                : "scale-100"
                                              }
                `}
                                          >
                                            <div className="text-white text-xs sm:text-sm md:text-base">
                                              {stat.icon}
                                            </div>
                                          </div>

                                          {/* Icon glow */}
                                          <div
                                            className={`
                  absolute inset-0
                  bg-gradient-to-br ${stat.color}
                  rounded-lg sm:rounded-xl
                  blur-lg
                  -z-10
                  transition-opacity duration-300
                  ${isHovered ? "opacity-40" : "opacity-10"}
                `}
                                          />
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">

                                          {/* Label */}
                                          <div className="flex items-center justify-between gap-2">
                                            <p
                                              className={`
                    text-[8px]
                    sm:text-[9px]
                    md:text-[10px]
                    font-semibold
                    uppercase
                    tracking-wide
                    truncate
                    transition-colors duration-300
                    ${isHovered
                                                  ? "text-cyan-300"
                                                  : "text-slate-400"
                                                }
                  `}
                                            >
                                              {stat.label}
                                            </p>

                                            {/* Small indicator */}
                                            {isHovered && (
                                              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)] flex-shrink-0" />
                                            )}
                                          </div>

                                          {/* Count */}
                                          <p
                                            className={`
                  text-base
                  sm:text-lg
                  md:text-xl
                  font-extrabold
                  leading-none
                  mt-0.5
                  transition-all duration-300
                  ${isHovered
                                                ? "text-cyan-300"
                                                : "text-white"
                                              }
                `}
                                          >
                                            {stat.count}
                                          </p>

                                          {/* Breakdown */}
                                          {breakdownArray.length > 0 && (
                                            <div className="mt-1 sm:mt-1.5">
                                              <p
                                                className={`
                      text-[7px]
                      sm:text-[8px]
                      md:text-[9px]
                      leading-relaxed
                      font-medium
                      transition-colors duration-300
                      ${isHovered
                                                    ? "text-slate-300"
                                                    : "text-slate-500"
                                                  }
                    `}
                                              >
                                                {isHovered
                                                  ? breakdownArray.join(" • ")
                                                  : breakdownArray.slice(0, 6).join(" • ")}

                                                {!isHovered && hasMoreThan6 && (
                                                  <span className="ml-1 text-cyan-400 font-bold">
                                                    +{extraCount} more
                                                  </span>
                                                )}
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {slide.type === "card" && (
                              <div className="w-full h-full flex items-center justify-center px-1 sm:px-2 md:px-3 lg:px-0">

                                <div
                                  className="
        group relative overflow-hidden

        w-[92%] max-w-[240px]
        sm:w-[88%] sm:max-w-[300px]
        md:w-[82%] md:max-w-[360px]
        lg:w-[90%] lg:max-w-none

        bg-gradient-to-br
        from-slate-800/90
        via-slate-800/70
        to-slate-900/90

        backdrop-blur-xl

        rounded-lg sm:rounded-xl lg:rounded-2xl

        border border-white/10
        hover:border-cyan-400/30

        p-1
        sm:p-1.5
        md:p-2
        lg:p-2.5

        flex flex-col
        items-center
        text-center

        gap-0.5
        sm:gap-1
        md:gap-1.5
        lg:gap-1.5

        shadow-lg
        shadow-black/20

        hover:-translate-y-0.5
        hover:shadow-xl
        hover:shadow-cyan-500/10

        transition-all duration-300
      "
                                >

                                  {/* BACKGROUND GLOW */}
                                  <div
                                    className={`
          absolute
          -top-12
          -right-12
          w-24 h-24
          rounded-full
          bg-gradient-to-br ${slide.color}
          blur-3xl
          opacity-10
          group-hover:opacity-20
          transition-opacity duration-500
        `}
                                  />

                                  <div
                                    className="
          absolute
          -bottom-12
          -left-12
          w-24 h-24
          rounded-full
          bg-cyan-500/10
          blur-3xl
        "
                                  />

                                  {/* AVATAR */}
                                  <div className="relative z-10">

                                    {slide.avatar ? (
                                      <img
                                        src={slide.avatar}
                                        alt={slide.title}
                                        className="
              relative

              w-7 h-7
              sm:w-9 sm:h-9
              md:w-11 md:h-11
              lg:w-14 lg:h-14

              rounded-full
              object-cover

              border
              sm:border-2
              border-white/70

              shadow-md

              group-hover:scale-105
              transition-transform duration-300
            "
                                      />
                                    ) : (
                                      <div
                                        className={`
              relative

              w-7 h-7
              sm:w-9 sm:h-9
              md:w-11 md:h-11
              lg:w-14 lg:h-14

              rounded-full

              bg-gradient-to-br ${slide.color}

              flex items-center justify-center

              border
              border-white/10

              shadow-md

              group-hover:scale-105
              transition-transform duration-300
            `}
                                      >
                                        {slide.icon &&
                                          React.createElement(icons[slide.icon], {
                                            className: `
                  text-white

                  w-3 h-3
                  sm:w-4 sm:h-4
                  md:w-5 md:h-5
                  lg:w-6 lg:h-6
                `,
                                          })}
                                      </div>
                                    )}

                                    {/* ICON BADGE */}
                                    {slide.icon && (
                                      <div
                                        className={`
              absolute

              -bottom-0.5
              -right-0.5

              w-3 h-3
              sm:w-3.5 sm:h-3.5
              md:w-4 md:h-4
              lg:w-5 lg:h-5

              rounded-full

              bg-gradient-to-br ${slide.color}

              flex items-center justify-center

              border
              border-slate-900

              shadow-md
            `}
                                      >
                                        {React.createElement(icons[slide.icon], {
                                          className: `
                text-white

                w-1.5 h-1.5
                sm:w-2 sm:h-2
                md:w-2.5 md:h-2.5
                lg:w-2.5 lg:h-2.5
              `,
                                        })}
                                      </div>
                                    )}
                                  </div>

                                  {/* TITLE + SUBTITLE */}
                                  <div className="relative z-10 w-full">

                                    <h3
                                      className="
            text-white
            font-bold

            text-[9px]
            sm:text-[10px]
            md:text-xs
            lg:text-base

            leading-tight
            truncate
            max-w-full

            bg-gradient-to-r
            from-white
            via-cyan-100
            to-cyan-300

            bg-clip-text
            text-transparent
          "
                                    >
                                      {slide.title}
                                    </h3>

                                    {slide.subtitle && (
                                      <p
                                        className="
              text-cyan-400

              text-[6px]
              sm:text-[7px]
              md:text-[8px]
              lg:text-[10px]

              leading-tight
              font-medium

              mt-0.5
            "
                                      >
                                        {slide.subtitle}
                                      </p>
                                    )}
                                  </div>

                                  {/* SOCIAL ICONS */}
                                  {slide.socials?.length > 0 && (
                                    <div
                                      className="
            relative z-10

            flex
            items-center
            justify-center

            gap-1
            sm:gap-1.5
            md:gap-2
            lg:gap-2.5
          "
                                    >
                                      {slide.socials.map(({ icon, color }, index) => {
                                        const Icon = icons[icon];

                                        return (
                                          Icon && (
                                            <div
                                              key={index}
                                              className="
                    flex
                    items-center
                    justify-center

                    w-4 h-4
                    sm:w-5 sm:h-5
                    md:w-6 md:h-6
                    lg:w-7 lg:h-7

                    rounded-full

                    bg-white/5
                    border border-white/5

                    hover:bg-white/10
                    hover:scale-110

                    transition-all duration-200
                  "
                                            >
                                              <Icon
                                                style={{ color }}
                                                className="
                      w-2 h-2
                      sm:w-2.5 sm:h-2.5
                      md:w-3 md:h-3
                      lg:w-3.5 lg:h-3.5
                    "
                                              />
                                            </div>
                                          )
                                        );
                                      })}
                                    </div>
                                  )}

                                  {/* DIVIDER */}
                                  <div
                                    className="
          relative z-10

          w-full
          h-px

          bg-gradient-to-r
          from-transparent
          via-slate-600/70
          to-transparent
        "
                                  />

                                  {/* DESCRIPTION */}
                                  {slide.description && (
                                    <p
                                      className="
            relative z-10

            w-full
            text-left

            text-slate-300

            text-[6px]
            sm:text-[7px]
            md:text-[8px]
            lg:text-[10px]

            leading-tight
          "
                                    >
                                      {slide.description
                                        .split(" ")
                                        .slice(0, 33)
                                        .join(" ")}

                                      {slide.description.split(" ").length > 33 && "... "}

                                      {slide.showButton && (
                                        <span
                                          onClick={() => handleViewClick(slide.target)}
                                          className="
                text-cyan-400
                font-semibold

                cursor-pointer

                hover:text-cyan-300
                hover:underline

                ml-0.5

                transition-colors
              "
                                        >
                                          {slide.buttonText || "View"} →
                                        </span>
                                      )}
                                    </p>
                                  )}

                                  {/* STATS */}
                                  {slide.items?.length > 0 && (
                                    <div
                                      className="
            relative z-10

            grid
            grid-cols-3

            w-full

            gap-0.5
            sm:gap-1
            md:gap-1.5
          "
                                    >
                                      {slide.items.map((item, index) => (
                                        <div
                                          key={index}
                                          className="
                bg-white/[0.04]
                hover:bg-white/[0.08]

                border
                border-white/5
                hover:border-cyan-400/20

                rounded

                p-0.5
                sm:p-1
                md:p-1.5

                transition-all
                duration-200

                hover:-translate-y-0.5
              "
                                        >
                                          <p
                                            className="
                  text-white
                  font-bold
                  leading-none

                  text-[6px]
                  sm:text-[7px]
                  md:text-[8px]
                  lg:text-[10px]
                "
                                          >
                                            {item.value}
                                          </p>

                                          <p
                                            className="
                  text-slate-500
                  truncate

                  text-[5px]
                  sm:text-[5px]
                  md:text-[6px]
                  lg:text-[7px]

                  mt-0.5
                "
                                          >
                                            {item.label}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* BOTTOM ACCENT */}
                                  <div
                                    className={`
          relative z-10

          h-0.5
          w-6
          sm:w-8

          rounded-full

          bg-gradient-to-r ${slide.color}

          opacity-60

          group-hover:w-12

          transition-all duration-300
        `}
                                  />

                                </div>
                              </div>
                            )}           </div>
                        );
                      })}
                    </div>


                    {/* Slide Indicators */}
                    <div className="flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-3 md:py-3 flex-shrink-0 relative z-100 bg-gradient-to-t from-slate-900/50 to-transparent">
                      {laptopSlides.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentSlide(index)}
                          className={`transition-all duration-300 rounded-full ${currentSlide === index
                            ? "bg-cyan-400 w-6 sm:w-7 md:w-8 h-1.5 sm:h-1.5 md:h-2"
                            : "bg-slate-600 w-1.5 sm:w-2 md:w-2 h-1.5 sm:h-1.5 md:h-2 hover:bg-slate-500"
                            }`}
                          aria-label={`Go to slide ${index + 1}`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* CAMERA NOTCH */}
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-50">
                    <div className="bg-slate-950 rounded-b-2xl px-8 sm:px-10 md:px-12 lg:px-14 py-1 sm:py-1.5 flex items-center justify-center shadow-2xl border-x border-b border-slate-500/50">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-2.5 md:h-2.5 rounded-full bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-500 flex items-center justify-center shadow-inner">
                        <div className="w-[3px] h-[3px] sm:w-1 sm:h-1 rounded-full bg-cyan-400/60"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Laptop Base/Keyboard */}
                <div className="relative">
                  <div className="h-3 sm:h-4 md:h-5 lg:h-6 bg-gradient-to-b from-slate-700 via-slate-750 to-slate-800 rounded-b-lg sm:rounded-b-xl md:rounded-b-2xl shadow-lg relative overflow-hidden mt-0.5">
                    <div className="absolute inset-0 opacity-30 p-0.5 sm:p-1">
                      <div className="grid grid-cols-12 gap-[1px] sm:gap-[2px] h-full">
                        {[...Array(36)].map((_, i) => (
                          <div
                            key={i}
                            className="bg-slate-900/50 rounded-[1px]"
                          ></div>
                        ))}
                      </div>
                    </div>
                    <div className="absolute top-0 left-0 right-0 h-[2px] sm:h-[3px] bg-gradient-to-r from-transparent via-slate-600/50 to-transparent"></div>
                    <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-slate-900/30 transform -translate-y-1/2"></div>
                  </div>

                  <div
                    className="h-1.5 sm:h-2 md:h-2.5 lg:h-3 bg-gradient-to-b from-slate-800 via-slate-850 to-slate-900 shadow-2xl relative mt-0.5"
                    style={{
                      clipPath: "polygon(8% 0%, 92% 0%, 100% 100%, 0% 100%)",
                    }}
                  >
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-b from-slate-950/50 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-slate-950/80"></div>
                  </div>

                  <div className="flex justify-between absolute -bottom-[2px] left-0 right-0 px-6 sm:px-10 md:px-16">
                    <div className="w-3 sm:w-4 md:w-5 h-[2px] sm:h-[3px] bg-slate-950 rounded-full shadow-inner"></div>
                    <div className="w-3 sm:w-4 md:w-5 h-[2px] sm:h-[3px] bg-slate-950 rounded-full shadow-inner"></div>
                  </div>
                </div>
              </div>

              {/* Blur Effect */}
              <div
                className="absolute -inset-2 sm:-inset-3 md:-inset-4 bg-gradient-to-r from-blue-600 via-cyan-600 to-purple-600 rounded-xl sm:rounded-2xl md:rounded-3xl blur-lg sm:blur-xl md:blur-2xl opacity-20 group-hover:opacity-30 transition duration-500 -z-10"
                style={{
                  maxWidth: "100%",
                  left: "2%",
                  right: "2%",
                }}
              ></div>
            </div>
          </div>
        </section>

        {/* Scroll Down Icon */}
        <div
          className={`fixed bottom-[85px] sm:bottom-[90px] lg:bottom-8 left-1/2 -translate-x-1/2 transition-all duration-500 cursor-pointer z-40 ${isScrollIconVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10 pointer-events-none"
            }`}
          onClick={scrollToAbout}
        >
          <div className="flex flex-col items-center gap-2 group">
            <span className="text-slate-400 text-xs sm:text-sm font-medium group-hover:text-cyan-400 transition-colors duration-300">
              Scroll Down
            </span>

            <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-slate-400 rounded-full flex items-start justify-center p-1.5 sm:p-2 group-hover:border-cyan-400 transition-colors duration-300">
              <div className="w-1 h-2 sm:w-1.5 sm:h-3 bg-cyan-400 rounded-full animate-scroll"></div>
            </div>
          </div>
        </div>
      </main>
      <style jsx>{`
        @keyframes scroll {
          0% {
            transform: translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateY(12px);
            opacity: 0;
          }
        }
.glass-sweep {
  position: absolute;
  top: -60%;
  left: -150%;
  width: 70%;
  height: 220%;
  pointer-events: none;
  z-index: 30;

  background: linear-gradient(
    120deg,
    transparent 0%,
    rgba(180,220,255,0.12) 45%,
    rgba(180,220,255,0.25) 50%,
    rgba(180,220,255,0.12) 55%,
    transparent 100%
  );

  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  transform: rotate(14deg);
  opacity: 0;

  animation: glassSimple 10s ease-in-out infinite;
}

@keyframes glassSimple {
  /* wait */
  0% {
    left: -150%;
    opacity: 0;
  }

  /* ease in */
  10% {
    opacity: 0.45;
  }

  /* sweep */
  50% {
    left: 150%;
    opacity: 0.45;
  }

  /* ease out */
  65% {
    opacity: 0;
  }

  /* instant reset */
  66% {
    left: -150%;
    opacity: 0;
  }

  /* idle */
  100% {
    left: -150%;
    opacity: 0;
  }
}

        @keyframes float {
          0%,
          100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.3;
          }
          50% {
            transform: translate(var(--float-x), var(--float-y)) scale(1.2);
            opacity: 0.6;
          }
        }
@keyframes blinkFadeSoft {
  0% {
    opacity: 0.5;   /* visible idle state */
  }
  20% {
    opacity: 1;     /* highlight */
  }
  40% {
    opacity: 0.5;   /* fade but visible */
  }
  100% {
    opacity: 0.5;
  }
}
/* === AUTO GLASS SWEEP (DEBUG-SAFE) === */
.glass-sweep-auto {
  position: absolute;
  top: -60%;
  left: -150%;
  width: 50%;
  height: 220%;

  pointer-events: none;
  z-index: 30; /* ABOVE background, BELOW content */

background: linear-gradient(
  120deg,
  transparent 0%,
  rgba(255,255,255,0.04) 45%,
  rgba(255,255,255,0.08) 50%,
  rgba(255,255,255,0.04) 55%,
  transparent 100%
);



  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);

  transform: rotate(15deg);
  opacity: 0;

  animation: glassSweepAuto 8s ease-in-out infinite;
}
@keyframes backgroundFade {
  0% {
    opacity: 0.95;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.95;
  }
}

.bg-fade-animation {
  animation: backgroundFade 18s ease-in-out infinite;
}


@keyframes glassSweepAuto {
  0% {
    left: -150%;
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  35% {
    left: 150%;
    opacity: 1;
  }
  45% {
    opacity: 0;
  }
  100% {
    left: 150%;
    opacity: 0;
  }
}


.blink-dot {
  width: 4px;
  height: 4px;
  background-color: #34d399; /* emerald-400 */
  border-radius: 9999px;
  animation: blinkFadeSoft 1.5s infinite ease-in-out;
}

/* Sequential timing */
.d1 { animation-delay: 0s; }
.d2 { animation-delay: 0.5s; }
.d3 { animation-delay: 1s; }

        .animate-scroll {
          animation: scroll 1.5s ease-in-out infinite;
        }

        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* Animated Dots Particles */
        .dot-particle {
          position: absolute;
          width: 4px;
          height: 4px;
          background: radial-gradient(
            circle,
            rgba(42, 115, 205, 0.8) 0%,
            rgba(19, 180, 205, 0.4) 100%
          );
          border-radius: 50%;
          animation: float var(--duration) ease-in-out infinite;
          --float-x: 20px;
          --float-y: 30px;
        }

        .dot-1 {
          top: 10%;
          left: 15%;
          --duration: 8s;
          --float-x: 30px;
          --float-y: -25px;
          animation-delay: 0s;
        }

        .dot-2 {
          top: 25%;
          left: 75%;
          --duration: 10s;
          --float-x: -40px;
          --float-y: 35px;
          animation-delay: 1s;
        }

        .dot-3 {
          top: 45%;
          left: 20%;
          --duration: 12s;
          --float-x: 25px;
          --float-y: -30px;
          animation-delay: 2s;
        }

        .dot-4 {
          top: 60%;
          left: 85%;
          --duration: 9s;
          --float-x: -35px;
          --float-y: 28px;
          animation-delay: 1.5s;
        }

        .dot-5 {
          top: 75%;
          left: 40%;
          --duration: 11s;
          --float-x: 32px;
          --float-y: -22px;
          animation-delay: 3s;
        }

        .dot-6 {
          top: 20%;
          left: 50%;
          --duration: 13s;
          --float-x: -28px;
          --float-y: 40px;
          animation-delay: 0.5s;
        }

        .dot-7 {
          top: 35%;
          left: 90%;
          width: 3px;
          height: 3px;
          --duration: 10s;
          --float-x: -45px;
          --float-y: -20px;
          animation-delay: 2.5s;
        }

        .dot-8 {
          top: 55%;
          left: 10%;
          width: 5px;
          height: 5px;
          --duration: 14s;
          --float-x: 38px;
          --float-y: 32px;
          animation-delay: 1.8s;
        }

        .dot-9 {
          top: 80%;
          left: 70%;
          --duration: 9.5s;
          --float-x: -30px;
          --float-y: -35px;
          animation-delay: 0.8s;
        }

        .dot-10 {
          top: 15%;
          left: 35%;
          width: 3px;
          height: 3px;
          --duration: 11.5s;
          --float-x: 42px;
          --float-y: 25px;
          animation-delay: 3.5s;
        }
      `}</style>
    </div>
  );
};

export default Home;
